import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { PickerRange } from "@/utils/date-range";
import { format, isAfter, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon, X } from "lucide-react";
import { useState } from "react";
import { DateRange as CalendarDateRange } from "react-day-picker";

import { RANGE_DISPLAY_FORMAT } from "../constants";
import { PAYROLL_VOUCHER_LABELS } from "../messages";

interface DraftRange {
  from: Date;
  to?: Date;
}

interface VoucherDateRangePickerProps {
  value?: PickerRange;
  onChange: (range?: PickerRange) => void;
  onClear: () => void;
  className?: string;
}

const TRIGGER_CLASS_NAME: string =
  "w-[300px] justify-start text-left font-normal bg-[#2E0D1E] hover:bg-[#2E0D1E]/90 text-white";

const CALENDAR_MONTHS: number = 2;

const asDisplayDate = (day: Date): string =>
  format(day, RANGE_DISPLAY_FORMAT, { locale: ptBR });

const triggerLabelOf = (range?: DraftRange): string => {
  if (!range?.from) return PAYROLL_VOUCHER_LABELS.PERIOD_PLACEHOLDER;

  if (!range.to) return asDisplayDate(range.from);

  return `${asDisplayDate(range.from)} - ${asDisplayDate(range.to)}`;
};

const isFutureDay = (day: Date): boolean =>
  isAfter(startOfDay(day), startOfDay(new Date()));

export function VoucherDateRangePicker({
  value,
  onChange,
  onClear,
  className,
}: VoucherDateRangePickerProps): JSX.Element {
  const [draft, setDraft] = useState<DraftRange | undefined>(value);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const shown: DraftRange | undefined = isOpen ? draft : value;

  const handleOpenChange = (open: boolean): void => {
    if (open) setDraft(value);

    setIsOpen(open);
  };

  const handleSelect = (range: CalendarDateRange | undefined): void => {
    if (!range?.from) {
      setDraft(undefined);
      onChange(undefined);
      return;
    }

    setDraft({ from: range.from, to: range.to });

    if (!range.to) return;

    onChange({ from: range.from, to: range.to });
    setIsOpen(false);
  };

  const handleClear = (): void => {
    setDraft(undefined);
    setIsOpen(false);
    onClear();
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button id="voucher-date-range" className={TRIGGER_CLASS_NAME}>
            <CalendarIcon className="mr-2 h-4 w-4" />
            {triggerLabelOf(shown)}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0"
          align="start"
          side="bottom"
          avoidCollisions
          collisionPadding={16}
          sticky="always"
        >
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={shown?.from}
            selected={shown}
            onSelect={handleSelect}
            numberOfMonths={CALENDAR_MONTHS}
            disabled={isFutureDay}
            locale={ptBR}
          />
          <div className="flex justify-end border-t border-gray-200 p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 text-gray-700"
              aria-label={PAYROLL_VOUCHER_LABELS.CLEAR_PERIOD}
              onClick={handleClear}
            >
              <X className="mr-1 h-3.5 w-3.5" />
              {PAYROLL_VOUCHER_LABELS.CLEAR_PERIOD}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
